import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReportBuilder = ({ template, reportData, onReportDataChange }) => {
  const [activeSection, setActiveSection] = useState(0);

  const sectionTemplates = {
    'ransomware': [
      { id: 'executive-summary', title: 'Executive Summary', icon: 'FileText', required: true },
      { id: 'attack-timeline', title: 'Attack Timeline', icon: 'Clock', required: true },
      { id: 'payment-analysis', title: 'Payment Analysis', icon: 'DollarSign', required: true },
      { id: 'fund-tracing', title: 'Fund Tracing', icon: 'GitBranch', required: true },
      { id: 'recovery-recommendations', title: 'Recovery Recommendations', icon: 'Target', required: false }
    ],
    'exchange-theft': [
      { id: 'incident-overview', title: 'Incident Overview', icon: 'AlertTriangle', required: true },
      { id: 'breach-analysis', title: 'Breach Analysis', icon: 'Shield', required: true },
      { id: 'stolen-assets', title: 'Stolen Assets Tracking', icon: 'Search', required: true },
      { id: 'laundering-patterns', title: 'Laundering Patterns', icon: 'Shuffle', required: true },
      { id: 'attribution', title: 'Attribution Analysis', icon: 'Users', required: false }
    ],
    'money-laundering': [
      { id: 'scheme-overview', title: 'Scheme Overview', icon: 'Eye', required: true },
      { id: 'transaction-patterns', title: 'Transaction Patterns', icon: 'BarChart3', required: true },
      { id: 'mixing-services', title: 'Mixing Services', icon: 'Shuffle', required: true },
      { id: 'cashout-methods', title: 'Cash-out Methods', icon: 'CreditCard', required: true },
      { id: 'legal-analysis', title: 'Legal Analysis', icon: 'Scale', required: false }
    ],
    'default': [
      { id: 'executive-summary', title: 'Executive Summary', icon: 'FileText', required: true },
      { id: 'methodology', title: 'Methodology', icon: 'Settings', required: true },
      { id: 'findings', title: 'Key Findings', icon: 'Search', required: true },
      { id: 'evidence', title: 'Evidence Analysis', icon: 'Database', required: true },
      { id: 'conclusions', title: 'Conclusions', icon: 'CheckCircle', required: false }
    ]
  };

  const sections = sectionTemplates?.[template] || sectionTemplates?.default;

  const handleSectionDataChange = (sectionId, field, value) => {
    const updatedData = {
      ...reportData,
      sections: {
        ...reportData?.sections,
        [sectionId]: {
          ...reportData?.sections?.[sectionId],
          [field]: value
        }
      }
    };
    onReportDataChange(updatedData);
  };

  const renderSectionEditor = (section) => {
    const sectionData = reportData?.sections?.[section?.id] || {};

    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3 pb-4 border-b border-border">
          <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
            <Icon name={section?.icon} size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{section?.title}</h3>
            <p className="text-sm text-text-secondary">
              {section?.required ? 'Required section' : 'Optional section'}
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <Input
            label="Section Title"
            type="text"
            placeholder={`Enter ${section?.title?.toLowerCase()} title`}
            value={sectionData?.title || ''}
            onChange={(e) => handleSectionDataChange(section?.id, 'title', e?.target?.value)}
          />

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Content
            </label>
            <textarea
              className="w-full h-40 px-3 py-2 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder={`Enter ${section?.title?.toLowerCase()} content...`}
              value={sectionData?.content || ''}
              onChange={(e) => handleSectionDataChange(section?.id, 'content', e?.target?.value)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Key Points"
              type="text"
              placeholder="Add key points (comma separated)"
              value={sectionData?.keyPoints || ''}
              onChange={(e) => handleSectionDataChange(section?.id, 'keyPoints', e?.target?.value)}
            />
            <Input
              label="Evidence References"
              type="text"
              placeholder="Reference evidence items"
              value={sectionData?.evidenceRefs || ''}
              onChange={(e) => handleSectionDataChange(section?.id, 'evidenceRefs', e?.target?.value)}
            />
          </div>

          {section?.id === 'fund-tracing' && (
            <div className="p-4 bg-muted rounded-lg">
              <h4 className="font-medium text-text-primary mb-3">Transaction Data Import</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Button variant="outline" size="sm">
                  <Icon name="Download" size={16} color="currentColor" className="mr-2" />
                  Import Transactions
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="BarChart3" size={16} color="currentColor" className="mr-2" />
                  Import Charts
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="GitBranch" size={16} color="currentColor" className="mr-2" />
                  Import Flow Maps
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-surface rounded-lg border border-border">
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-text-primary">Report Builder</h3>
          <p className="text-sm text-text-secondary mt-1">Build your forensic report section by section</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-text-secondary">
            Section {activeSection + 1} of {sections?.length}
          </span>
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              disabled={activeSection === 0}
              onClick={() => setActiveSection(Math.max(0, activeSection - 1))}
            >
              <Icon name="ChevronLeft" size={16} color="currentColor" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={activeSection === sections?.length - 1}
              onClick={() => setActiveSection(Math.min(sections?.length - 1, activeSection + 1))}
            >
              <Icon name="ChevronRight" size={16} color="currentColor" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex">
        {/* Section Navigation */}
        <div className="w-64 border-r border-border p-4">
          <h4 className="font-medium text-text-primary mb-3">Sections</h4>
          <div className="space-y-2">
            {sections?.map((section, index) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(index)}
                className={`
                  w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-forensic
                  ${activeSection === index
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted text-text-secondary hover:text-text-primary'
                  }
                `}
              >
                <Icon name={section?.icon} size={16} color="currentColor" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{section?.title}</div>
                  {section?.required && (
                    <div className="text-xs opacity-75">Required</div>
                  )}
                </div>
                {reportData?.sections?.[section?.id]?.content && (
                  <Icon name="Check" size={14} color="var(--color-success)" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Section Editor */}
        <div className="flex-1 p-6">
          {renderSectionEditor(sections?.[activeSection])}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;